import { useMemo, useRef, useState } from 'react';
import type { MeterFrame } from '@shared/protocol';
import { getPalette, useCanvas } from '../../lib/canvas';
import { PeakBallistics, fmtDb, meterScale, scalePosition, type MeterMode } from '../../lib/meterMath';
import type { LiveRefs } from '../../lib/useStreamLive';
import { Panel, Segmented } from '../Panel';

const OVER_HOLD_MS = 3000;
const SCALE_W = 34;

/** Shared geometry so the DOM legend lines up with the bars drawn on the canvas. */
export function meterLayout(n: number) {
  const barW = n <= 2 ? 40 : n <= 8 ? 26 : 14;
  const gap = n <= 8 ? 6 : 3;
  return { barW, gap, width: SCALE_W + n * barW + (n - 1) * gap + 2 };
}

/** All channels of all tracks, flattened for the meter bridge. */
function flatten(frame: MeterFrame | null) {
  const tracks = frame?.tracks ?? [];
  const multi = tracks.length > 1;
  const labels: string[] = [];
  const out = { samplePeak: [] as (number | null)[], truePeak: [] as (number | null)[], ppm: [] as (number | null)[], truePeakMax: [] as (number | null)[], clipCount: [] as number[] };
  for (const t of tracks) {
    t.labels.forEach((l) => labels.push(multi ? `${t.track + 1}:${l}` : l));
    out.samplePeak.push(...t.samplePeak);
    out.truePeak.push(...t.truePeak);
    out.ppm.push(...t.ppm);
    out.truePeakMax.push(...t.truePeakMax);
    out.clipCount.push(...t.clipCount);
  }
  return { labels, ...out, channels: labels.length };
}

function valuesFor(f: ReturnType<typeof flatten>, mode: MeterMode): (number | null)[] {
  if (mode === 'sample') return f.samplePeak;
  if (mode === 'truepeak') return f.truePeak;
  return f.ppm;
}

export function LevelMeters({
  live,
  frame,
  maxTruePeak,
}: {
  live: React.RefObject<LiveRefs>;
  frame: MeterFrame | null;
  maxTruePeak: number;
}) {
  const [mode, setMode] = useState<MeterMode>('truepeak');
  const scale = useMemo(() => meterScale(mode, maxTruePeak), [mode, maxTruePeak]);
  const ballistics = useRef<PeakBallistics[]>([]);
  const overUntil = useRef<number[]>([]);
  const modeRef = useRef(mode);
  if (modeRef.current !== mode) {
    modeRef.current = mode;
    ballistics.current = [];
  }

  const flat = flatten(frame);
  const channels = Math.max(1, flat.channels || 2);
  const labels = flat.labels.length ? flat.labels : ['L', 'R'];
  const layout = meterLayout(channels);

  const canvasRef = useCanvas((ctx, w, h, now) => {
    const p = getPalette();
    const lf = live.current?.frame ? flatten(live.current.frame) : null;
    const n = lf?.channels || channels;
    const isPpm = mode === 'ppm-ebu' || mode === 'ppm-bbc';
    while (ballistics.current.length < n) {
      // PPM readings already carry IEC Type II ballistics from the server; only smooth the 10 Hz steps.
      ballistics.current.push(isPpm ? new PeakBallistics(24 / 2.8, 0) : new PeakBallistics());
    }
    const values = lf ? valuesFor(lf, mode) : [];

    ctx.clearRect(0, 0, w, h);
    const { barW, gap } = meterLayout(n);
    const scaleW = SCALE_W;
    const top = 8;
    const bottom = h - 8;
    const totalW = n * barW + (n - 1) * gap;
    const x0 = scaleW;
    const yOf = (db: number) => bottom - scalePosition(scale, db) * (bottom - top);

    // Scale
    ctx.font = `11px ${p.font}`;
    ctx.fillStyle = p.scale;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    let lastLabelY = -Infinity;
    for (const t of scale.ticks) {
      if (t.db < scale.min || t.db > scale.max) continue;
      const y = Math.round(yOf(t.db)) + 0.5;
      // Skip labels that would collide with the previous one.
      if (Math.abs(y - lastLabelY) < 13) continue;
      lastLabelY = y;
      ctx.fillText(t.label, scaleW - 6, y);
      ctx.strokeStyle = p.divider;
      ctx.beginPath();
      ctx.moveTo(scaleW - 3, y);
      ctx.lineTo(x0 + totalW, y);
      ctx.stroke();
    }

    const yWarn = yOf(scale.warnFrom);
    const yOver = yOf(scale.overFrom);
    for (let c = 0; c < n; c++) {
      const x = x0 + c * (barW + gap);
      const b = ballistics.current[c];
      b.update(now, values[c] ?? null);
      const v = values[c];
      if (v !== null && v !== undefined && v >= scale.overFrom) overUntil.current[c] = now + OVER_HOLD_MS;

      ctx.fillStyle = p.track;
      ctx.fillRect(x, top, barW, bottom - top);
      const yv = yOf(b.value);
      // Colour zones: green to warn, amber to over, red above.
      const seg = (from: number, to: number, color: string) => {
        const a = Math.max(from, yv);
        if (a < to) {
          ctx.fillStyle = color;
          ctx.fillRect(x, a, barW, to - a);
        }
      };
      seg(yWarn, bottom, p.ok);
      seg(yOver, yWarn, p.warn);
      seg(top, yOver, p.over);

      if (!isPpm && Number.isFinite(b.hold)) {
        const yh = Math.round(yOf(b.hold));
        ctx.fillStyle = b.hold >= scale.overFrom ? p.over : p.hold;
        ctx.fillRect(x, yh - 1, barW, 2);
      }
    }
  });

  const peakText = (c: number) => {
    if (!frame) return '--';
    if (mode === 'truepeak') return fmtDb(flat.truePeakMax[c]);
    return fmtDb(valuesFor(flat, mode)[c]);
  };
  const now = performance.now();

  return (
    <Panel
      title={channels > 2 ? `Level meters (${channels} channels)` : 'Level meters'}
      actions={
        <Segmented<MeterMode>
          label="Meter type"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'sample', label: 'dBFS' },
            { value: 'truepeak', label: 'dBTP' },
            { value: 'ppm-ebu', label: 'PPM EBU' },
            { value: 'ppm-bbc', label: 'PPM BBC' },
          ]}
        />
      }
    >
      <div className="level-meters">
        <canvas
          ref={canvasRef}
          className="level-canvas"
          style={{ width: layout.width }}
          aria-label={`${scale.name} meters`}
          role="img"
        />
        <div
          className="level-legend"
          style={{ gridTemplateColumns: `repeat(${channels}, ${layout.barW}px)`, columnGap: layout.gap, marginLeft: SCALE_W }}
        >
          {labels.map((label, c) => {
            const over = (overUntil.current[c] ?? 0) > now;
            return (
              <div key={c} className="level-channel">
                <span className="level-label">{label}</span>
                {channels <= 8 && <span className="num level-value">{peakText(c)}</span>}
                {channels <= 8 && (
                  <span className={`over-led ${over ? 'on' : ''}`} aria-live="polite">
                    {over ? 'OVER' : 'ok'}
                  </span>
                )}
                {frame && flat.clipCount[c] > 0 && <span className="clip-count num">{flat.clipCount[c]} clip</span>}
              </div>
            );
          })}
        </div>
        <p className="muted small">
          {scale.name} ({scale.unit}).{' '}
          {mode === 'truepeak' ? `Value shows the maximum since reset. Red above ${maxTruePeak} dBTP.` : null}
          {mode === 'sample' ? `Red above ${maxTruePeak} dBFS.` : null}
          {mode === 'ppm-ebu' ? 'TEST = -18 dBFS alignment; red above +9 (permitted maximum level).' : null}
          {mode === 'ppm-bbc' ? 'Mark 4 = -18 dBFS alignment, 4 dB per mark; red above PPM 6.' : null}
        </p>
      </div>
    </Panel>
  );
}
